/**
 * PackagePDP - Package Product Detail Page
 *
 * VERSION STRUCTURE:
 * ==================
 * This file contains both V2 (legacy) and V3 (modern) designs, controlled by the "v3" feature flag.
 *
 * TO REMOVE V2 (when fully migrated to V3):
 * 1. Delete the entire V2 DESIGN block (marked with "V2 DESIGN (LEGACY - TO BE REMOVED)")
 * 2. Delete the V2 ADDITIONAL SECTIONS block (marked with "V2 ADDITIONAL SECTIONS (LEGACY)")
 * 3. Remove the `isV3Enabled` check and keep only the V3 content
 * 4. Keep the TOP NAVIGATION and SHARED MODALS sections - they are used by both versions
 * 5. Clean up any V2-specific imports (Carousel, OverviewPanel, PricingPreview, SelectTreatmentPlan, KeyBenefits, WhatToExpectList, SavingsInfo, dayjs)
 *
 * All sections are clearly marked with comment headers for easy identification.
 */

import {
  Elements,
  PaymentMethodMessagingElement,
} from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import cx from "classnames";
import dayjs from "dayjs";
import { useEffect, useReducer, useState } from "react";
import AnimateHeight from "react-animate-height";
import {
  LoaderFunction,
  redirect,
  useLoaderData,
  useNavigate,
} from "react-router-dom";

import { AdonisChatCTAInPDP } from "@/modules/adonis/AdonisChatCTAInPDP/AdonisChatCTAInPDP";
import {
  CURRENT_ORDER_QUERY_KEY,
  invalidateCurrentOrderDetails,
} from "@/modules/cart/useCartDetails";
import { get, post } from "api";
import { Button } from "atoms/button/Button";
import Carousel from "atoms/carousel/Carousel";
import { Checkbox } from "atoms/checkbox/Checkbox";
import { Icon } from "atoms/icon/Icon";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import ChevronRight from "atoms/icon/icons/chevron_right.svg";
import CheckIcon from "atoms/icon/icons/ic_added_to_cart_check_ol.svg";
import OfferIcon from "atoms/icon/icons/ic_offers_ol.svg";
import { ImageCarousel } from "atoms/ImageCarousel/ImageCarousel";
import { InfoBox } from "atoms/info-box/InfoBox";
import { OverviewPanel } from "atoms/overview-panel/OverviewPanel";
import { DescriptiveTag } from "atoms/tag/DescriptiveTag";
import { TermsAndCondition } from "atoms/terms-and-condition/TermsAndCondition";
import { TextButton } from "atoms/text-button/TextButton";
import useHistory from "hooks/useHistory";
import { useAdonisChat } from "modules/adonis/AdonisChatProvider/AdonisChatProvider";
import { stripeLoader, useStripePublishableKey } from "modules/stripe";
import { AccordionList } from "molecules/accordion-list/AccordionList";
import { DropdownSelector } from "molecules/containers/dropdown-selector/DropdownSelector";
import { ExpandableItems } from "molecules/expandable-items/ExpandableItems";
import TopNav from "molecules/navbars/TopNav";
import { AddedToCartModal } from "organisms/added-to-cart-modal/AddedToCartModal";
import { KeyBenefits } from "organisms/key-benefits/KeyBenefits";
import { ServiceFeeRow } from "organisms/order-summary/ServiceFeeRow";
import ProductCarousel from "organisms/product-carousel/ProductCarousel";
import { ModalSelectorList } from "organisms/selection-modals/modal-selector-list/ModalSelectorList";
import { WhatToExpectList } from "organisms/what-to-expect-list/WhatToExpectList";
import { queryClient } from "pages/Root";
import { PricingPreview } from "pages/root/shop/PackagePDP/pricing-preview/PricingPreview";
import { SelectTreatmentPlan } from "pages/root/shop/PackagePDP/select-treatment-plan/SelectTreatmentPlan";
import { useFeatureFlag } from "services/Flags";
import {
  getPDPBannerDiscountText,
  getProductTitle,
} from "services/HelperService";
import {
  getDiscountCustomColor,
  getListingsWithDiscountInformation,
  hasLinearPricing,
} from "services/ListingHelperService";
import Services from "services/Services";
import { currencyService } from "toolbox/CurrencyService";
import { notNull } from "toolbox/Guards";
import { formatCurrency } from "toolbox/Money";
import { waitObj } from "toolbox/Promise";
import { FormattedString } from "utils/FormattedStrings";
import pluralize from "utils/pluralize";

import { SavingsInfo } from "./savings-info/SavingsInfo";

const DESCRIPTION_LENGTH_LIMIT = 456;

type Action =
  | { type: "package"; packageId: string | null; priceId: string | null }
  | { type: "price"; priceId: string }
  | { type: "open"; modal: "package" | "price" | "cart" }
  | { type: "close" };

type State = {
  packageId: string | null | undefined;
  priceId: string | null | undefined;
  modal: "package" | "price" | "cart" | null;
  membershipBoxChecked: boolean;
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "package":
      return { ...state, packageId: action.packageId, priceId: action.priceId };

    case "price":
      return { ...state, priceId: action.priceId };

    case "open":
      return { ...state, modal: action.modal };

    case "close":
      return { ...state, modal: null };
  }
};

const loader = async (id: string) => {
  const shopData = queryClient.ensureQueryData({
    queryKey: ["/shop"],
    queryFn: () => get("/shop"),
    staleTime: 1000 * 60 * 2,
  });

  const platform = await queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );

  const platformLocations = queryClient.fetchQuery(
    ["/multi-platform/platform/{platformId}/locations"],
    () =>
      get("/multi-platform/platform/{platformId}/locations", {
        platformId: platform.id,
      })
  );

  const listings = queryClient.fetchQuery(["/listings"], () =>
    get("/listings")
  );

  const listing = queryClient.fetchQuery(["/listings/{id}", { id }], () =>
    get("/listings/{id}", { id })
  );

  const expectations = queryClient.fetchQuery(
    ["/listings/{id}/expectations", { id }],
    () => get("/listings/{id}/expectations", { id })
  );

  const beforeAfters = queryClient.fetchQuery(
    ["/listings/{id}/before-afters", { id }],
    () => get("/listings/{id}/before-afters", { id })
  );

  const media = queryClient.fetchQuery(["/listings/{id}/media", { id }], () =>
    get("/listings/{id}/media", { id })
  );

  const packages = queryClient.fetchQuery(
    ["/listings/{id}/packages", { id }],
    () => get("/listings/{id}/packages", { id })
  );

  const related = queryClient.fetchQuery(
    ["/listings/{id}/related", { id }],
    () => get("/listings/{id}/related", { id })
  );

  const platformUserMemberships = queryClient.fetchQuery(
    ["/platform-user-memberships"],
    () => get("/platform-user-memberships")
  );

  const shopDiscounts = queryClient.fetchQuery(
    ["/shop/discounts"],
    () => get("/shop/discounts"),
    { staleTime: 1000 * 10 }
  );

  const shopDiscountsListing = queryClient.fetchQuery(
    ["/shop/discounts/listings/{listingId}", { listingId: id }],
    () => get("/shop/discounts/listings/{listingId}", { listingId: id }),
    { staleTime: 1000 * 10 }
  );

  const customShopCategories = queryClient.fetchQuery(
    ["/shop/custom-categories/listings"],
    () => get("/shop/custom-categories/listings"),
    { staleTime: 1000 * 10 }
  );

  const user = queryClient.fetchQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });

  const giftCards = queryClient.fetchQuery(["/gift-cards"], () =>
    get("/gift-cards", {})
  );

  return waitObj({
    listings,
    beforeAfters,
    expectations,
    listing,
    media,
    packages,
    platform,
    platformLocations,
    related,
    platformUserMemberships,
    shopData,
    shopDiscounts,
    shopDiscountsListing,
    customShopCategories,
    user,
    giftCards,
  });
};

export type LoaderData = Awaited<ReturnType<typeof loader>>;
export const packagePDPLoader: LoaderFunction = async ({ params: { id } }) => {
  if (!id) return redirect("..");

  try {
    const data = await loader(id);
    if (data.listing.acquired) {
      return redirect("..");
    }

    if (data.giftCards.some((gc) => gc.id === data.listing.id)) {
      return redirect(`/shop/gift-cards`);
    }

    return data;
  } catch (error) {
    // If something goes wrong and the user is a custom plan created user,
    // redirect them to the sign-in page
    const user = await queryClient
      .fetchQuery({
        queryKey: ["/user"],
        queryFn: () => get("/user"),
      })
      .catch(() => null);

    if (user && user.hasBeenPreRegistered && !user.completedProfile) {
      await Services.auth.clearAll();
      return redirect("/signin");
    }
    console.error("Error handling the exception:", error);
    return redirect("..");
  }
};

const singlePackage = (data: LoaderData) =>
  data.packages.length == 1 ? data.packages[0].id : null;

const singlePrice = (data: LoaderData, packageId: string | null) => {
  const $package = data.packages.find(({ id }) => id == packageId);

  return ($package && $package.packagePrices.length === 1) ||
    ($package && hasLinearPricing($package.packagePrices))
    ? $package.packagePrices[0].id
    : null;
};

const PackagePDP = () => {
  const queryClient = useQueryClient();
  const { routeBackNumber, setRouteBackNumber } = useHistory();
  const data = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const isV3Enabled = true;
  const isUnitedDerm = useFeatureFlag("unitedDerm");
  const stripeKey = useStripePublishableKey();
  const stripePromise = stripeLoader.getStripe(stripeKey);
  const currency = currencyService.getCurrency();
  const {
    isEnabled: isAdonisEnabled,
    isPersonalisationEnabled,
    persona,
    openChatWithMessage,
  } = useAdonisChat();
  const [state, dispatch] = useReducer(reducer, {
    packageId: singlePackage(data),
    priceId: singlePrice(data, singlePackage(data)),
    modal: null,
    membershipBoxChecked: false,
  });
  const [showFullDescription, setShowFullDescription] = useState(false);

  const isCustomPlan = !!data.listing.platformUserId;
  const isMember = data.platformUserMemberships.length > 0;
  const $package = data.packages.find(({ id }) => id === state.packageId);
  const price = $package?.packagePrices.find(({ id }) => id === state.priceId);
  const packageItems = data.packages.flatMap(({ items }) => items).reverse();
  const defaultPrice = data.packages
    .flatMap(({ packagePrices }) => packagePrices)
    .find(({ price }) => price === data.listing.baseCost);
  const cost = price?.price ?? $package?.baseCost ?? data.listing.baseCost;
  const singularUnitType =
    $package?.packageUnitType.singularUnitType ??
    data.listing.packageUnitType?.singularUnitType ??
    "unit";

  const bestDiscountForListing =
    data.shopDiscounts?.listingDiscounts?.[data.listing.id];

  let fullDiscount = bestDiscountForListing;

  if (fullDiscount) {
    data.packages.forEach((p) => {
      const packageDiscount = data.shopDiscountsListing.packageDiscounts[p.id];
      if (
        packageDiscount?.discount &&
        packageDiscount?.title === bestDiscountForListing?.title &&
        fullDiscount
      ) {
        fullDiscount = {
          ...fullDiscount,
          discount: packageDiscount.discount,
        };
      }
    });
  }

  const discountText =
    bestDiscountForListing &&
    fullDiscount &&
    getPDPBannerDiscountText(
      fullDiscount,
      data.listing.type,
      data.listings,
      data.giftCards
    );
  const discountColor = getDiscountCustomColor(
    bestDiscountForListing,
    data.customShopCategories
  );

  useEffect(() => {
    dispatch({
      type: "package",
      packageId: singlePackage(data),
      priceId: singlePrice(data, singlePackage(data)),
    });
  }, [data]);

  const packagesWithDiscount = getListingsWithDiscountInformation(
    data.packages,
    data.customShopCategories,
    data.shopDiscountsListing.packageDiscounts
  );

  const packagesWithDiscountDict = Object.fromEntries(
    packagesWithDiscount.map((x) => [x.id, x])
  );

  const relatedCards = getListingsWithDiscountInformation(
    data.related,
    data.customShopCategories,
    data.shopDiscounts?.listingDiscounts
  )
    .map(
      (item) =>
        item &&
        item.imageUrl &&
        item.type &&
        item.title &&
        item.baseCost && {
          to: `/pdp/package/${item.id}`,
          imageSrc: item.imageUrl,
          tag: item.type,
          title: item.title,
          price: item.baseCost,
          memberPrice: item.membershipCost,
          discount: item.discountText,
          customColor: item.customColor,
        }
    )
    .filter(notNull) as {
    to: string;
    imageSrc: string;
    tag: string;
    title: string;
    price: number;
    memberPrice?: number;
  }[];

  const secondButtonProps =
    data.packages.length > 1
      ? {
          secondButtonLabel: "< Back",
          onSecondButtonClick: () =>
            dispatch({ type: "open", modal: "package" }),
        }
      : {};

  const isCustomPlanCreatedUser =
    !data.user.completedProfile && data.user.hasBeenPreRegistered;

  const shouldShowAICTA =
    isV3Enabled &&
    isAdonisEnabled &&
    isPersonalisationEnabled &&
    !isCustomPlanCreatedUser &&
    !data.user.isGuest;

  const isPartnerEmrBookingEnabled =
    data.platform.platformSettings?.partnerEmrBookingEnabled;

  const isPackageBookable = () => {
    if (!$package || !isPartnerEmrBookingEnabled) return false;

    // Multi-service packages are not bookable
    if ($package.items.length > 1) return false;

    // If package.zenotiId === product.zenotiId, it's a bookable service
    const firstItem = $package.items[0];
    return (
      $package.zenotiId != null &&
      $package.zenotiId === firstItem?.productZenotiId
    );
  };

  const shouldShowBookingFlow = isPackageBookable();

  const getPrimaryButtonLabel = () => {
    if (data.user.isGuest) return "Sign up to add to cart";
    if (data.listing.active) {
      if (isCustomPlan) return "Checkout now";
      return shouldShowBookingFlow ? "Book Now" : "Add to cart";
    }
    return "No longer available";
  };

  const needsTruncation =
    (data.listing.description || "").length > DESCRIPTION_LENGTH_LIMIT;

  // Get concerns as array for key benefits display
  const concernsList =
    data.shopData.concerns
      ?.filter(({ id }) => data.listing.concernIds.includes(id))
      .map(({ name }) => name)
      .filter(notNull) || [];

  // Create accordion items for pre-care and post-care only
  const accordionItems = data.expectations
    .filter((exp) => exp.title && exp.description)
    .map((exp) => ({
      title: exp.title,
      children: (
        <div className="text-justify text-body2 text-primary">
          {exp.description}
        </div>
      ),
      titleClassName: "font-serif text-h4 font-semibold text-primary",
    }));

  // Create image slides for ImageCarousel
  const imageSlides = data.media.map((mediaItem, index) => ({
    src: typeof mediaItem === "string" ? mediaItem : mediaItem.url,
    alt: `${data.listing.title} - Image ${index + 1}`,
  }));

  return (
    <>
      {isV3Enabled ? (
        /* ========================================
           V3 DESIGN (MODERN LUXURY DESIGN)
           ========================================
           This is the new design that matches ProductPDP.
           When removing V2, delete the else block below and
           remove the isV3Enabled check.
           ======================================== */
        <div className="flex h-full min-h-screen flex-col">
          {/* ========================================
              TOP NAVIGATION
              ======================================== */}
          {isCustomPlanCreatedUser ? (
            <TopNav showHeaderItems={false}>
              {data.platform.logoImageUrl && (
                <img
                  alt="Platform logo."
                  src={data.platform.logoImageUrl}
                  className="h-8"
                />
              )}
            </TopNav>
          ) : (
            <TopNav
              startIconSVG={ChevronLeft}
              onStartIconClick={() => navigate(routeBackNumber)}
            >
              <div className="flex max-h-[32px] items-center">
                <h1 className="text-xl">
                  {data.listing.type == "Treatment" ? "Treatments" : "Packages"}
                </h1>
              </div>
            </TopNav>
          )}

          <div className="flex flex-1 flex-col overflow-y-auto bg-white">
            {/* Header Section */}
            <div className="flex flex-col items-center gap-2 pb-2 pt-6">
              <div className="text-center font-serif text-h2 text-primary">
                {data.listing.title}
              </div>
            </div>

            {/* Pricing Section with /unit type */}
            <div className="px-4">
              <div className="flex flex-col items-center pb-4">
                {data.user.isGuest && (
                  <div className="flex flex-col items-center text-body1">
                    <span>Sign up to see price</span>
                  </div>
                )}
                {cost > 0 && (
                  <>
                    {/* Price display with /unit only on main price */}
                    {price?.membershipPrice ?? data.listing.membershipCost ? (
                      <div className="text-body1">
                        <span className="pr-2">
                          {!state.priceId && data.listing.type !== "Package"
                            ? "From "
                            : ""}
                          {formatCurrency(cost)}
                          {!!state.priceId &&
                            data.listing.type !== "Package" &&
                            `/${singularUnitType}`}
                        </span>
                        <span className="border-l border-black pl-2 text-bold1 text-primary">
                          {formatCurrency(
                            price?.membershipPrice ??
                              data.listing.membershipCost ??
                              0
                          )}{" "}
                          member
                        </span>
                      </div>
                    ) : (
                      <div className="text-body1">
                        {!state.priceId && data.listing.type !== "Package"
                          ? "From "
                          : ""}
                        <span className="text-bold1">
                          {formatCurrency(cost)}
                          {!!state.priceId &&
                            data.listing.type !== "Package" &&
                            `/${singularUnitType}`}
                        </span>
                      </div>
                    )}

                    {data.platform.platformSettings?.affirmEnabled && (
                      <div>
                        <Elements
                          stripe={stripePromise}
                          options={{
                            appearance: {
                              variables: {
                                fontSizeBase: "14px",
                                colorText: "#000000",
                              },
                              rules: {
                                ".PaymentMethodMessaging": {
                                  textAlign: "center",
                                },
                              },
                            },
                          }}
                        >
                          <PaymentMethodMessagingElement
                            className={
                              cost > 5000
                                ? "rounded-xl bg-background-two py-1"
                                : ""
                            }
                            options={{
                              amount: isMember
                                ? price?.membershipPrice ??
                                  data.listing.membershipCost ??
                                  cost
                                : cost,
                              currency: currency.code as "USD" | "CAD",
                              paymentMethodTypes: ["affirm"],
                              countryCode:
                                currency.code === "CAD" ? "CA" : "US",
                            }}
                          />
                        </Elements>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Image Section */}
            {data.media.length > 0 && (
              <div className="relative z-0 flex flex-col items-center pb-2">
                <ImageCarousel
                  slides={imageSlides}
                  productName={data.listing.title}
                />
              </div>
            )}

            {/* Discount Banner */}
            {!isCustomPlanCreatedUser &&
              bestDiscountForListing &&
              !data.user.isGuest && (
                <div className="px-4 pt-4">
                  <InfoBox
                    text={discountText}
                    type="discount"
                    leftIcon={OfferIcon}
                    customColor={discountColor ?? undefined}
                  />
                </div>
              )}

            {/* Custom Package Exclusive Card */}
            {isCustomPlan && (
              <div className="px-4 pb-6 pt-4">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-tint via-white to-brand-tint p-6 shadow-lg ring-1 ring-brand-color/20">
                  {/* Decorative corner accents */}
                  <div className="absolute right-0 top-0 h-24 w-24 -translate-y-8 translate-x-8 rounded-full bg-gradient-to-br from-brand-color/10 to-transparent blur-2xl" />
                  <div className="absolute bottom-0 left-0 h-20 w-20 -translate-x-8 translate-y-8 rounded-full bg-gradient-to-tr from-brand-color/10 to-transparent blur-2xl" />

                  <div className="relative flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <div className="h-0.5 w-7 rounded-full bg-gradient-to-r from-brand-color to-brand-color/60" />
                      <span className="text-xs font-semibold uppercase tracking-wide text-brand-color">
                        Custom Package for {data.user.firstName || "You"}
                      </span>
                    </div>

                    <div className="text-h4 font-serif font-semibold leading-snug text-primary">
                      This custom package was crafted exclusively for you
                      {data.listing.createdBy &&
                        ` by ${data.listing.createdBy.fullName}`}
                    </div>

                    {!!data.listing.expiresOn && (
                      <div className="flex items-start gap-2 rounded-lg bg-white/60 p-3">
                        <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-brand-tint">
                          <span className="text-xs">⏱</span>
                        </div>
                        <div className="text-body2 text-secondary">
                          <span className="text-bold2 text-brand-color">
                            Reserved until{" "}
                            {dayjs(data.listing.expiresOn).format(
                              "MMMM D, YYYY"
                            )}
                          </span>
                          {" — "}
                          Secure your personalized package before this offer
                          expires
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Details Section */}
            <div className="px-2">
              {/* Helps With - Key Benefits Style */}
              {concernsList.length > 0 && (
                <div className="p-4">
                  {concernsList.length <= 5 ? (
                    <>
                      <div className="text-h4 mb-2 font-serif font-semibold text-primary">
                        Helps With
                      </div>
                      <div className="flex flex-col gap-1 text-body2">
                        {concernsList.map((concern, index) => (
                          <div
                            key={`concern-${index}`}
                            className="flex flex-row items-center gap-2"
                          >
                            <Icon
                              svg={CheckIcon}
                              size="x2small"
                              color="brandColor"
                              className="mt-0.5 flex-shrink-0"
                            />
                            <span className="flex-1">{concern}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mb-2 flex flex-row items-center gap-2">
                        <Icon
                          svg={CheckIcon}
                          size="x2small"
                          color="brandColor"
                          className="mt-0.5 flex-shrink-0"
                        />
                        <span className="text-h4 font-serif font-semibold text-primary">
                          Helps With
                        </span>
                      </div>
                      <div className="text-body2">
                        {concernsList.join(", ")}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Description */}
              <div className={`p-4 ${isCustomPlan ? "bg-background-one" : ""}`}>
                <div className="flex flex-col gap-2">
                  <div
                    className={`text-h4 font-serif font-semibold text-primary ${
                      isCustomPlan ? "text-center" : ""
                    }`}
                  >
                    {isCustomPlan ? "What's Included" : "Description"}
                  </div>
                  <div className="mb-2">
                    <AnimateHeight
                      height={
                        showFullDescription || !needsTruncation ? "auto" : 55
                      }
                      duration={300}
                      easing="ease-in-out"
                    >
                      <div
                        className={`break-words [&_p]:text-body2 ${
                          isCustomPlan ? "text-center text-body1" : ""
                        }`}
                      >
                        <FormattedString>
                          {data.listing.description}
                        </FormattedString>
                      </div>
                    </AnimateHeight>
                  </div>
                  {needsTruncation && (
                    <button
                      onClick={() =>
                        setShowFullDescription(!showFullDescription)
                      }
                      className="flex flex-col items-center"
                    >
                      {showFullDescription && (
                        <Icon
                          svg={ChevronLeft}
                          size="small"
                          className="rotate-90"
                        />
                      )}
                      <span className="text-bold2">
                        {showFullDescription ? "Show less" : "Show more"}
                      </span>
                      {!showFullDescription && (
                        <Icon
                          svg={ChevronRight}
                          size="small"
                          className="rotate-90"
                        />
                      )}
                    </button>
                  )}
                </div>

                {/* AI CTA Button */}
                {shouldShowAICTA && (
                  <div className="flex items-center justify-center py-8">
                    <AdonisChatCTAInPDP
                      variant={persona.name === "Adonis" ? "adonis" : "aria"}
                      onClick={() => {
                        const message = `Let's chat about ${data.listing.title} {{context:/pdp/package/${data.listing.id}}}`;
                        openChatWithMessage(message);
                      }}
                    />
                  </div>
                )}

                {/* Custom Plan Metadata - Services & Created Date */}
                {isCustomPlan && (
                  <div className="mt-4 flex flex-col gap-2 border-t border-gray-200 pt-4">
                    {data.platformLocations.locations.length > 1 &&
                      !!data.user.platformLocation?.name && (
                        <div className="text-center text-body2 text-secondary">
                          Services to be booked at{" "}
                          <span className="text-bold2">
                            {data.user.platformLocation.name}
                          </span>
                        </div>
                      )}
                    <div className="text-center text-body3 italic text-secondary">
                      Created on{" "}
                      {dayjs(data.listing.created).format("MMMM D, YYYY")}
                    </div>
                  </div>
                )}
              </div>

              {/* Accordion for Pre/Post Care Instructions */}
              {accordionItems.length > 0 && (
                <div className="-mx-2">
                  <AccordionList items={accordionItems} />
                </div>
              )}

              {packageItems && data.listing.type === "Package" && (
                <div className="p-4">
                  <div className="text-h4 mb-2 font-serif font-semibold text-primary">
                    What&apos;s Included
                  </div>
                  <div className="flex flex-col gap-2">
                    {packageItems.map((packageItem, index) => (
                      <div
                        key={`package-item-${index}`}
                        className="flex w-full items-center overflow-hidden rounded-lg border border-l-8 border-l-gray-200 bg-white shadow-subtle"
                      >
                        <div className="flex flex-1 flex-col overflow-hidden py-2 pl-4 pr-2">
                          <div className="flex items-baseline gap-1">
                            {packageItem.quantity > 1 && (
                              <span className="font-serif text-body2 font-semibold text-brand-color">
                                {packageItem.quantity}x
                              </span>
                            )}
                            <div className="truncate font-serif text-body2 font-semibold">
                              {packageItem.name}
                            </div>
                          </div>
                          {packageItem.description && (
                            <div className="line-clamp-5 text-body3 text-secondary">
                              {packageItem.description}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Add membership to cart directly */}
            {!isCustomPlanCreatedUser &&
              !data.user.isGuest &&
              !isMember &&
              !!(price?.membershipPrice ?? data.listing.membershipCost) && (
                <div className="px-4 pt-4">
                  <div className="flex w-full items-center gap-4 rounded bg-brand-tint px-3">
                    <div className="flex">
                      <Checkbox
                        onChange={() => {
                          state.membershipBoxChecked =
                            !state.membershipBoxChecked;
                        }}
                      />
                      <div className="flex flex-col pt-4">
                        <div className="flex flex-col pl-3">
                          <div className="flex items-center gap-1 text-bold2">
                            Become a
                            <DescriptiveTag color="secondary">
                              {(price?.membership?.name ??
                                defaultPrice?.membership?.name ??
                                "") + " Member"}
                            </DescriptiveTag>
                          </div>
                          <div className="pt-3 text-body2">
                            Add to cart now to{" "}
                            <span className="text-bold2">
                              save{" "}
                              {formatCurrency(
                                cost -
                                  (price?.membershipPrice ??
                                    data.listing.membershipCost ??
                                    0)
                              )}
                            </span>{" "}
                            on this{" "}
                            {data.listing.type === "Package"
                              ? "package"
                              : "treatment"}{" "}
                            today, plus monthly benefits
                          </div>
                          <div className="pt-1 text-body3 text-secondary">
                            {formatCurrency(
                              (price?.membership ?? defaultPrice?.membership)
                                ?.monthlyCost ?? 0
                            )}
                            /month membership fee applies
                          </div>
                        </div>
                        <div>
                          <TextButton
                            onClick={() => {
                              const membership =
                                price?.membership ?? defaultPrice?.membership;
                              if (membership?.id) {
                                navigate(
                                  `/pdp/membership/${membership.id}/${data.listing.type}`
                                );
                              }
                            }}
                          >
                            <div className="pb-1 text-sub2 uppercase text-brand-color">
                              View more info
                            </div>
                          </TextButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* Related Packages */}
            {relatedCards.length > 0 && data.listing.type === "Treatment" && (
              <div className="bg-one">
                <ProductCarousel
                  title="RELATED PACKAGES"
                  cards={relatedCards}
                  guestMode={data.user.isGuest}
                />
              </div>
            )}

            {/* Terms and Conditions */}
            <div className="px-4 pt-4">
              <TermsAndCondition
                hideText={!data.platform.platformSettings?.affirmEnabled}
              />
            </div>

            {/* Convenience Fee Notice */}
            {data.platform.platformSettings?.patientFeePercentage != null &&
              data.platform.platformSettings.patientFeePercentage > 0 && (
                <div className="px-4 pb-4">
                  <ServiceFeeRow
                    title={
                      !isUnitedDerm
                        ? "Convenience fee applied at checkout"
                        : "Credit card fee applied at checkout"
                    }
                  />
                </div>
              )}
          </div>

          {/* Bottom Sticky Section with Compact Dropdowns and CTA */}
          {isCustomPlan ? (
            // Custom Plan Checkout Button
            !data.listing.acquired &&
            !data.listing.expired && (
              <div className="sticky bottom-[calc(59px_+_env(safe-area-inset-bottom)-1px)] w-full">
                {/* Gradient fade overlay for seamless transition */}
                <div className="pointer-events-none absolute inset-x-0 -top-8 h-8 bg-gradient-to-t from-white to-transparent" />

                <div className="relative bg-white px-4 pb-4 pt-2">
                  <Button
                    fullWidth
                    disabled={!data.listing.active}
                    onClick={async () => {
                      if (state.packageId && state.priceId) {
                        const { packageId, priceId } = state;

                        if (shouldShowBookingFlow) {
                          const queryParams = new URLSearchParams({
                            packageId,
                            priceId,
                          });
                          navigate(
                            `/booking/${
                              data.listing.id
                            }/providers?${queryParams.toString()}`
                          );
                        } else {
                          await post("/orders/current/lineitems", {
                            packageListingId: data.listing.id,
                            packageId,
                            packagePriceId: priceId,
                            membershipId: null,
                          });

                          queryClient.invalidateQueries({
                            queryKey: ["/orders/current/summary"],
                          });

                          dispatch({ type: "open", modal: "cart" });
                        }
                      }
                    }}
                  >
                    {getPrimaryButtonLabel()}
                  </Button>
                </div>
              </div>
            )
          ) : (
            <div className="sticky bottom-[calc(59px_+_env(safe-area-inset-bottom)-1px)] w-full">
              {/* Gradient fade overlay for seamless transition */}
              <div className="pointer-events-none absolute inset-x-0 -top-8 h-8 bg-gradient-to-t from-white to-transparent" />

              <div className="relative bg-white">
                {!data.user.isGuest && (
                  <div className="flex flex-col gap-2 px-4 pt-2">
                    {/* Compact Package Selector - same height as button */}
                    {data.packages.length === 1 ? (
                      $package?.packagePrices?.length &&
                      $package?.packagePrices?.length > 1 && (
                        <>
                          {price ? (
                            <div className="[&>div]:flex [&>div]:!h-12 [&>div]:!min-h-0 [&>div]:items-center [&>div]:!rounded-xl [&>div]:!py-0">
                              <DropdownSelector
                                text={`${price.quantity} ${
                                  price.quantity > 1
                                    ? $package?.packageUnitType
                                        .pluralUnitType ?? "units"
                                    : $package?.packageUnitType
                                        .singularUnitType ?? "unit"
                                }`}
                                state="selected"
                                onClick={() =>
                                  dispatch({ type: "open", modal: "price" })
                                }
                              />
                            </div>
                          ) : (
                            <div className="[&>div]:flex [&>div]:!h-12 [&>div]:!min-h-0 [&>div]:items-center [&>div]:!rounded-xl [&>div]:!py-0">
                              <DropdownSelector
                                text={`Select number of ${
                                  $package?.packageUnitType.pluralUnitType ??
                                  "units"
                                }`}
                                state="unselected"
                                onClick={() =>
                                  dispatch({ type: "open", modal: "price" })
                                }
                              />
                            </div>
                          )}
                        </>
                      )
                    ) : $package ? (
                      <>
                        <div className="[&>div]:flex [&>div]:!h-12 [&>div]:!min-h-0 [&>div]:items-center [&>div]:!rounded-xl [&>div]:!py-0">
                          <DropdownSelector
                            text={$package.name}
                            secondaryText={undefined}
                            state="selected"
                            onClick={() =>
                              dispatch({ type: "open", modal: "package" })
                            }
                          />
                        </div>
                        {price ? (
                          <div className="[&>div]:flex [&>div]:!h-12 [&>div]:!min-h-0 [&>div]:items-center [&>div]:!rounded-xl [&>div]:!py-0">
                            <DropdownSelector
                              text={`${price.quantity} ${
                                price.quantity > 1
                                  ? $package.packageUnitType.pluralUnitType ??
                                    "units"
                                  : $package.packageUnitType.singularUnitType ??
                                    "unit"
                              }`}
                              state="selected"
                              onClick={() =>
                                dispatch({ type: "open", modal: "price" })
                              }
                            />
                          </div>
                        ) : (
                          <div className="[&>div]:flex [&>div]:!h-12 [&>div]:!min-h-0 [&>div]:items-center [&>div]:!rounded-xl [&>div]:!py-0">
                            <DropdownSelector
                              text={`Select number of ${
                                $package.packageUnitType.pluralUnitType ??
                                "units"
                              }`}
                              state="unselected"
                              onClick={() =>
                                dispatch({ type: "open", modal: "price" })
                              }
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="[&>div]:flex [&>div]:!h-12 [&>div]:!min-h-0 [&>div]:items-center [&>div]:!rounded-xl [&>div]:!py-0">
                        <DropdownSelector
                          text="Select treatment"
                          state="unselected"
                          onClick={() =>
                            dispatch({ type: "open", modal: "package" })
                          }
                        />
                      </div>
                    )}
                  </div>
                )}

                <div className="px-4 pb-4 pt-2">
                  <Button
                    fullWidth
                    disabled={!data.listing.active || data.user.isGuest}
                    onClick={async () => {
                      if (state.packageId && state.priceId) {
                        const { packageId, priceId, membershipBoxChecked } =
                          state;
                        const membershipId = membershipBoxChecked
                          ? price?.membership?.id
                          : null;
                        if (shouldShowBookingFlow) {
                          const queryParams = new URLSearchParams({
                            packageId,
                            priceId,
                            ...(membershipId && {
                              membershipId,
                            }),
                          });
                          navigate(
                            `/booking/${
                              data.listing.id
                            }/providers?${queryParams.toString()}`
                          );
                        } else {
                          await post("/orders/current/lineitems", {
                            packageListingId: data.listing.id,
                            packageId,
                            packagePriceId: priceId,
                            membershipId,
                          });

                          invalidateCurrentOrderDetails();
                          dispatch({ type: "open", modal: "cart" });
                        }
                      } else if (state.packageId) {
                        dispatch({ type: "open", modal: "price" });
                      } else {
                        dispatch({ type: "open", modal: "package" });
                      }
                    }}
                  >
                    {getPrimaryButtonLabel()}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* ========================================
           V2 DESIGN (LEGACY - TO BE REMOVED)
           ========================================
           This is the old design. Once V3 is fully
           rolled out, this entire else block can be
           safely deleted.
           ======================================== */
        <div className="flex h-full min-h-screen flex-col">
          {/* ========================================
              TOP NAVIGATION
              ======================================== */}
          {isCustomPlanCreatedUser ? (
            <TopNav showHeaderItems={false}>
              {data.platform.logoImageUrl && (
                <img
                  alt="Platform logo."
                  src={data.platform.logoImageUrl}
                  className="h-8"
                />
              )}
            </TopNav>
          ) : (
            <TopNav
              startIconSVG={ChevronLeft}
              onStartIconClick={() => navigate(routeBackNumber)}
            >
              <div className="flex max-h-[32px] items-center">
                <h1 className="text-xl">
                  {data.listing.type == "Treatment" ? "Treatments" : "Packages"}
                </h1>
              </div>
            </TopNav>
          )}

          <div className="flex flex-1 flex-col overflow-y-auto bg-one">
            <div className="flex flex-col gap-5 px-4 pb-8 pt-9">
              <div className="flex flex-col gap-6">
                <OverviewPanel title={data.listing.title} />
                {!data.user.isGuest ? (
                  <PricingPreview
                    affirmEnabled={
                      data.platform.platformSettings?.affirmEnabled
                    }
                    defaultPrice={cost}
                    memberPrice={
                      !isCustomPlanCreatedUser
                        ? price?.membershipPrice ??
                          data.listing.membershipCost ??
                          undefined
                        : undefined
                    }
                    isPriceSet={!!state.priceId}
                    isMember={isMember}
                    packageUnitType={singularUnitType}
                    type={data.listing.type}
                  />
                ) : (
                  <div className="flex flex-col items-center text-body1">
                    <span>Sign up to see price</span>
                  </div>
                )}
              </div>
              {!isCustomPlanCreatedUser &&
                !data.user.isGuest &&
                !!(price?.membershipPrice ?? data.listing.membershipCost) && (
                  <SavingsInfo
                    baseCost={cost}
                    memberCost={
                      price?.membershipPrice ?? data.listing.membershipCost ?? 0
                    }
                    membershipName={
                      price?.membership?.name ??
                      defaultPrice?.membership?.name ??
                      ""
                    }
                    isMember={isMember}
                    onViewMoreInfo={() => {
                      const membership =
                        price?.membership ?? defaultPrice?.membership;

                      if (membership?.id) {
                        navigate(
                          `/pdp/membership/${membership.id}/${data.listing.type}`
                        );
                      }
                    }}
                  />
                )}
              {!isCustomPlanCreatedUser &&
                bestDiscountForListing &&
                !data.user.isGuest && (
                  <InfoBox
                    text={discountText}
                    type="discount"
                    leftIcon={OfferIcon}
                    customColor={discountColor ?? undefined}
                  />
                )}
              {isCustomPlan && (
                <p className="text-center text-sub1 uppercase text-primary">
                  What&apos;s included
                </p>
              )}
              <div className="flex flex-col gap-2">
                {data.media.length > 0 && <Carousel slides={data.media} />}
                <div
                  className={cx(
                    "flex flex-col gap-5 px-4 text-body1 text-secondary",
                    data.media.length === 1 && "pt-5"
                  )}
                >
                  <FormattedString>{data.listing.description}</FormattedString>
                </div>
              </div>
              {isCustomPlan && (
                <>
                  {data.platformLocations.locations.length > 1 &&
                    !!data.user.platformLocation?.name && (
                      <p className="text-center text-body2 text-secondary">
                        Services to be booked at the{" "}
                        <strong>{data.user.platformLocation.name}</strong>{" "}
                        location
                      </p>
                    )}
                  <p className="text-center text-body2 text-secondary">
                    Created on{" "}
                    {dayjs(data.listing.created).format("MMM D, YYYY")}
                    {data.listing.createdBy &&
                      ` by ${data.listing.createdBy.fullName}`}
                    {!!data.listing.expiresOn && (
                      <strong className="mt-0.5 block">
                        Available for purchase until{" "}
                        {dayjs(data.listing.expiresOn).format("MMM D, YYYY")}
                      </strong>
                    )}
                  </p>
                </>
              )}
            </div>
            <div className="bg-white px-4 pb-4 pt-8">
              {!data.user.isGuest && (
                <SelectTreatmentPlan
                  disableDropdowns={isCustomPlan}
                  showCheckoutButton={
                    isCustomPlan &&
                    !data.listing.acquired &&
                    !data.listing.expired
                  }
                  onClickPlanSelector={() =>
                    dispatch({ type: "open", modal: "package" })
                  }
                  onClickSessionsSelector={() =>
                    dispatch({ type: "open", modal: "price" })
                  }
                  baseCost={cost}
                  isPriceSet={!!state.priceId}
                  isMember={isMember}
                  memberCost={
                    price?.membershipPrice ??
                    data.listing.membershipCost ??
                    undefined
                  }
                  serviceFee={
                    data.platform.platformSettings?.patientFeePercentage
                  }
                  productType={data.listing.type}
                  package={$package}
                  price={price ?? defaultPrice}
                  packages={data.packages}
                  onMemberInfo={() => {
                    state.membershipBoxChecked = !state.membershipBoxChecked;
                  }}
                  onViewMoreInfo={() => {
                    const membership =
                      price?.membership ?? defaultPrice?.membership;
                    if (membership?.id) {
                      navigate(
                        `/pdp/membership/${membership.id}/${data.listing.type}`
                      );
                    }
                  }}
                  isCustomPlanCreatedUser={isCustomPlanCreatedUser}
                />
              )}
            </div>
            <KeyBenefits
              keyBenefits={
                data.shopData.concerns
                  ?.filter(({ id }) => data.listing.concernIds.includes(id))
                  .map(({ name }) => name)
                  .filter(notNull) || []
              }
            />
            {packageItems && data.listing.type === "Package" && (
              <div className="bg-one p-6">
                <div className="pb-6 text-center text-sub1 text-brand-color">
                  WHAT IS INCLUDED?
                </div>
                <ExpandableItems
                  items={packageItems.map((packageItem) => ({
                    id: packageItem.name + packageItem.quantity,
                    title: `${packageItem.quantity}x ${packageItem.name}`,
                    description: packageItem.description,
                  }))}
                />
              </div>
            )}
            <WhatToExpectList expectations={data.expectations} />
            {relatedCards.length > 0 && data.listing.type === "Treatment" && (
              <div className="bg-one">
                <ProductCarousel
                  title="RELATED PACKAGES"
                  cards={relatedCards}
                  guestMode={data.user.isGuest}
                />
              </div>
            )}
            <div className="px-4">
              <TermsAndCondition
                hideText={!data.platform.platformSettings?.affirmEnabled}
              />
            </div>
          </div>

          {!isCustomPlan && (
            <div className="sticky bottom-[calc(59px_+_env(safe-area-inset-bottom)-1px)] w-full bg-white p-4">
              <Button
                fullWidth
                disabled={!data.listing.active || data.user.isGuest}
                onClick={async () => {
                  if (state.packageId && state.priceId) {
                    const { packageId, priceId, membershipBoxChecked } = state;
                    const membershipId = membershipBoxChecked
                      ? price?.membership?.id
                      : null;

                    if (shouldShowBookingFlow) {
                      const queryParams = new URLSearchParams({
                        packageId,
                        priceId,
                        ...(membershipId && { membershipId }),
                      });
                      navigate(
                        `/booking/${
                          data.listing.id
                        }/providers?${queryParams.toString()}`
                      );
                    } else {
                      await post("/orders/current/lineitems", {
                        packageListingId: data.listing.id,
                        packageId,
                        packagePriceId: priceId,
                        membershipId,
                      });

                      invalidateCurrentOrderDetails();
                      dispatch({ type: "open", modal: "cart" });
                    }
                  } else if (state.packageId) {
                    dispatch({ type: "open", modal: "price" });
                  } else {
                    dispatch({ type: "open", modal: "package" });
                  }
                }}
              >
                {getPrimaryButtonLabel()}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ========================================
           SHARED MODALS (USED BY BOTH V2 AND V3)
           ========================================
           These modals are shared between V2 and V3.
           Keep these when removing V2.
           ======================================== */}
      <ModalSelectorList
        size="fullscreen"
        title="SELECT YOUR SERVICE"
        open={state.modal === "package"}
        type="products"
        items={data.packages.map(
          ({ id, name, description, packagePrices, baseCost }) => ({
            id,
            title: name,
            description,
            membershipPrice: packagePrices[0].membershipPrice,
            priceOrResults: baseCost,
            tagText: packagesWithDiscountDict[id]?.discountText,
            tagColor: packagesWithDiscountDict[id]?.customColor,
          })
        )}
        checkedItems={$package ? [$package.id] : []}
        onClose={() => dispatch({ type: "close" })}
        onSelect={(packageId) => {
          /* Incrementing the routeBackNumber here is only necessary because the
            dispatch causes a re-render of AffirmMessageElement in PricePreview, which
            adds to window.history. See CMX-421 & CMX-826 */
          data.platform.platformSettings?.affirmEnabled &&
            setRouteBackNumber(() => routeBackNumber - 1);

          return dispatch({
            type: "package",
            packageId,
            priceId: singlePrice(data, packageId),
          });
        }}
        firstButtonLabel="Next"
        firstButtonDisabled={!state.packageId}
        onFirstButtonClick={() => dispatch({ type: "open", modal: "price" })}
        isVariantsModal
      />

      {$package && data.listing.active && (
        <ModalSelectorList
          size="medium"
          title="SELECT YOUR QUANTITY"
          type="products"
          open={state.modal === "price"}
          items={$package.packagePrices.map(
            ({ id, price, membershipPrice, quantity }) => ({
              id,
              priceOrResults: price,
              membershipPrice,
              title: `${quantity} ${pluralize(
                quantity,
                $package.packageUnitType.singularUnitType ?? "unit",
                $package.packageUnitType.pluralUnitType ?? "units"
              )}`,
            })
          )}
          checkedItems={price ? [price.id] : []}
          onSelect={(priceId) => {
            /* Incrementing the routeBackNumber here is only necessary because the
            dispatch causes a re-render of AffirmMessageElement in PricePreview, which
            adds to window.history. See CMX-421 & CMX-826 */
            data.platform.platformSettings?.affirmEnabled &&
              setRouteBackNumber(() => routeBackNumber - 1);

            return dispatch({ type: "price", priceId });
          }}
          onClose={() => dispatch({ type: "close" })}
          firstButtonLabel={shouldShowBookingFlow ? "Book Now" : "Add to cart"}
          firstButtonDisabled={!$package || !price}
          onFirstButtonClick={async () => {
            if (!$package || !price) {
              return;
            }

            const packageId = $package.id;
            const priceId = price.id;
            const membershipId = state.membershipBoxChecked
              ? price?.membership?.id
              : null;

            if (shouldShowBookingFlow) {
              const queryParams = new URLSearchParams({
                packageId,
                priceId,
                ...(membershipId && { membershipId }),
              });
              navigate(
                `/booking/${
                  data.listing.id
                }/providers?${queryParams.toString()}`
              );
            } else {
              await post("/orders/current/lineitems", {
                packageListingId: data.listing.id,
                packageId,
                packagePriceId: priceId,
                membershipId,
              });

              invalidateCurrentOrderDetails();
              dispatch({ type: "open", modal: "cart" });
            }
          }}
          {...secondButtonProps}
        />
      )}

      {$package && price && (
        <AddedToCartModal
          title={getProductTitle(data.listing.title, $package.name)}
          open={state.modal === "cart"}
          subtitle={
            price.quantity === 1
              ? `${price.quantity} ${$package.packageUnitType.singularUnitType}`
              : `${price.quantity} ${$package.packageUnitType.pluralUnitType}`
          }
          cost={
            (state.membershipBoxChecked || isMember) &&
            price.membershipPrice != null
              ? price.membershipPrice
              : price.price
          }
          membership={state.membershipBoxChecked ? price?.membership : null}
          onClose={() => dispatch({ type: "close" })}
        />
      )}
    </>
  );
};

export default PackagePDP;
